/**
 * Default Formatting Functions for Schema-Driven Table Component
 * Provides common data formatters for various data types
 * Part of T033-T035 - Phase 6 implementation
 */

/**
 * Currency formatter with configurable options
 * @param {number} value - The numeric value to format
 * @param {object} options - Formatting options
 * @returns {string} Formatted currency string
 */
export function formatCurrency(value, options = {}) {
  const {
    currency = 'GBP',
    locale = 'en-GB', 
    minimumFractionDigits = 2,
    maximumFractionDigits = 2,
    showSymbol = true
  } = options

  if (value === null || value === undefined || isNaN(value)) {
    return showSymbol ? '£0.00' : '0.00'
  }

  try {
    const formatted = new Intl.NumberFormat(locale, {
      style: showSymbol ? 'currency' : 'decimal',
      currency: currency,
      minimumFractionDigits,
      maximumFractionDigits
    }).format(Number(value))

    return formatted
  } catch (error) {
    console.warn('Currency formatting failed:', error)
    return `£${Number(value).toFixed(2)}`
  }
}

/**
 * Date formatter with multiple format options
 * @param {Date|string|number} value - The date value to format
 * @param {object} options - Formatting options
 * @returns {string} Formatted date string
 */
export function formatDate(value, options = {}) {
  const {
    format = 'short', // 'short', 'long', 'time', 'datetime', 'relative', 'custom'
    locale = 'en-GB',
    customFormat = null,
    showTime = false
  } = options

  if (!value) return ''

  let date
  try {
    date = new Date(value)
    if (isNaN(date.getTime())) return 'Invalid Date'
  } catch (error) {
    return 'Invalid Date'
  }

  try {
    switch (format) {
      case 'short':
        return date.toLocaleDateString(locale)
      
      case 'long':
        return date.toLocaleDateString(locale, { 
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        })
      
      case 'time':
        return date.toLocaleTimeString(locale, {
          hour: '2-digit',
          minute: '2-digit'
        })
      
      case 'datetime':
        return date.toLocaleString(locale)
      
      case 'relative':
        return formatRelativeDate(date)
      
      case 'custom':
        return customFormat ? formatCustomDate(date, customFormat) : date.toISOString().split('T')[0]
      
      default:
        return showTime ? date.toLocaleString(locale) : date.toLocaleDateString(locale)
    }
  } catch (error) {
    console.warn('Date formatting failed:', error)
    return date.toISOString().split('T')[0]
  }
}

/**
 * Percentage formatter
 * @param {number} value - The numeric value to format as percentage
 * @param {object} options - Formatting options
 * @returns {string} Formatted percentage string
 */
export function formatPercentage(value, options = {}) {
  const {
    decimals = 1,
    multiplier = 1, // Set to 100 if value is already a percentage (0-100)
    showSymbol = true,
    locale = 'en-GB'
  } = options

  if (value === null || value === undefined || isNaN(value)) {
    return showSymbol ? '0%' : '0'
  }

  try {
    const numericValue = Number(value) * multiplier
    const formatted = new Intl.NumberFormat(locale, {
      style: showSymbol ? 'percent' : 'decimal',
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    }).format(numericValue / 100)

    return formatted
  } catch (error) {
    console.warn('Percentage formatting failed:', error)
    return `${Number(value * multiplier).toFixed(decimals)}%`
  }
}

/**
 * Number formatter with thousands separators and precision control
 * @param {number} value - The numeric value to format
 * @param {object} options - Formatting options
 * @returns {string} Formatted number string
 */
export function formatNumber(value, options = {}) {
  const {
    decimals = 0,
    thousandsSeparator = true,
    locale = 'en-GB',
    prefix = '',
    suffix = '',
    fallback = '0'
  } = options

  if (value === null || value === undefined || isNaN(value)) {
    return fallback
  }

  try {
    const formatted = new Intl.NumberFormat(locale, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
      useGrouping: thousandsSeparator
    }).format(Number(value))

    return `${prefix}${formatted}${suffix}`
  } catch (error) {
    console.warn('Number formatting failed:', error)
    return `${prefix}${Number(value).toFixed(decimals)}${suffix}`
  }
}

/**
 * Text truncation formatter
 * @param {string} value - The text value to truncate
 * @param {object} options - Formatting options
 * @returns {string} Truncated text with ellipsis
 */
export function formatText(value, options = {}) {
  const {
    maxLength = 50,
    ellipsis = '...',
    wordBoundary = true,
    preserveHtml = false
  } = options

  if (!value || typeof value !== 'string') {
    return ''
  }

  if (value.length <= maxLength) {
    return value
  }

  let truncated = value.substring(0, maxLength)

  if (wordBoundary) {
    const lastSpace = truncated.lastIndexOf(' ')
    if (lastSpace > 0 && lastSpace > maxLength * 0.6) { // Don't cut too aggressively
      truncated = truncated.substring(0, lastSpace)
    }
  }

  return truncated + ellipsis
}

/**
 * File size formatter
 * @param {number} bytes - The size in bytes
 * @param {object} options - Formatting options
 * @returns {string} Formatted file size string
 */
export function formatFileSize(bytes, options = {}) {
  const {
    decimals = 1,
    binary = false, // Use 1024 (binary) vs 1000 (decimal) base
    longUnits = false // Use 'Megabytes' vs 'MB'
  } = options

  if (bytes === 0) return '0 Bytes'
  if (bytes === null || bytes === undefined || isNaN(bytes)) return '0 Bytes'

  const base = binary ? 1024 : 1000
  const units = longUnits 
    ? ['Bytes', 'Kilobytes', 'Megabytes', 'Gigabytes', 'Terabytes']
    : ['B', 'KB', 'MB', 'GB', 'TB']

  const index = Math.floor(Math.log(Math.abs(bytes)) / Math.log(base))
  const size = bytes / Math.pow(base, index)

  return `${size.toFixed(decimals)} ${units[index] || 'B'}`
}

/**
 * Status badge formatter
 * @param {string|number} value - The status value
 * @param {object} options - Formatting options
 * @returns {string} HTML string for status badge
 */
export function formatStatus(value, options = {}) {
  const {
    mapping = {},
    defaultClass = 'secondary',
    showText = true,
    uppercase = false
  } = options

  if (!value) return ''

  const statusText = uppercase ? String(value).toUpperCase() : String(value)
  const statusClass = mapping[value] || mapping[value.toLowerCase()] || defaultClass

  if (!showText) return `<span class="badge badge-${statusClass}"></span>`
  
  return `<span class="badge badge-${statusClass}">${statusText}</span>`
}

/**
 * Array formatting (joins array elements)
 * @param {Array} value - The array to format
 * @param {object} options - Formatting options
 * @returns {string} Formatted array string
 */
export function formatArray(value, options = {}) {
  const {
    separator = ', ',
    maxItems = 3,
    moreText = 'more',
    emptyText = 'None'
  } = options

  if (!Array.isArray(value) || value.length === 0) {
    return emptyText
  }

  if (value.length <= maxItems) {
    return value.join(separator)
  }

  const visible = value.slice(0, maxItems)
  const remaining = value.length - maxItems

  return `${visible.join(separator)}${separator}+${remaining} ${moreText}`
}

// Helper functions

/**
 * Format relative date (e.g., "2 days ago", "in 3 hours")
 */
function formatRelativeDate(date) {
  const now = new Date()
  const diffMs = now - date
  const diffSecs = Math.round(diffMs / 1000)
  const diffMins = Math.round(diffSecs / 60)
  const diffHours = Math.round(diffMins / 60)
  const diffDays = Math.round(diffHours / 24)

  if (Math.abs(diffSecs) < 60) {
    return 'just now'
  } else if (Math.abs(diffMins) < 60) {
    return diffMins > 0 ? `${diffMins} minutes ago` : `in ${-diffMins} minutes`
  } else if (Math.abs(diffHours) < 24) {
    return diffHours > 0 ? `${diffHours} hours ago` : `in ${-diffHours} hours`
  } else if (Math.abs(diffDays) < 30) {
    return diffDays > 0 ? `${diffDays} days ago` : `in ${-diffDays} days`
  } else {
    return date.toLocaleDateString()
  }
}

/**
 * Format date with custom format string
 * Basic implementation - could be enhanced with a full date formatting library
 */
function formatCustomDate(date, format) {
  const map = {
    'YYYY': date.getFullYear(),
    'YY': String(date.getFullYear()).slice(-2),
    'MM': String(date.getMonth() + 1).padStart(2, '0'),
    'DD': String(date.getDate()).padStart(2, '0'),
    'HH': String(date.getHours()).padStart(2, '0'),
    'mm': String(date.getMinutes()).padStart(2, '0'),
    'ss': String(date.getSeconds()).padStart(2, '0')
  }

  return format.replace(/YYYY|YY|MM|DD|HH|mm|ss/g, match => map[match])
}

// Export all formatters as a collection
export const formatters = {
  currency: formatCurrency,
  date: formatDate,
  percentage: formatPercentage,
  number: formatNumber,
  text: formatText,
  fileSize: formatFileSize,
  status: formatStatus,
  array: formatArray
}

// Default export
export default formatters